import React, { useState, useEffect } from "react";
import {
  Mail,
  Phone,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Search,
  Users,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// -------------------------------------------------------
// Student Card Component
// -------------------------------------------------------
const StudentCard = ({ data }) => {
  const navigate = useNavigate();

  if (!data) {
    return (
      <div className="w-full bg-white rounded-lg p-4 text-red-500">
        Invalid student data
      </div>
    );
  }

  const {
    user_id = "N/A",
    full_name = "",
    first_name = "",
    last_name = "",
    email = "Not provided",
    mobile_number = "Not provided",
    avatar_url = "",
    course_name = "N/A",
    schedule_start_date = "",
  } = data;

  const displayName =
    first_name && last_name
      ? `${first_name} ${last_name}`
      : full_name || "Unknown User";

  const formattedDate = schedule_start_date
    ? new Date(schedule_start_date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "2-digit",
      })
    : "N/A";

  const handleViewDetails = () => {
    navigate(`/ins_student/student_details/${user_id}`, {
      state: { ...data },
    });
  };

  return (
    <div className="w-full flex flex-col bg-white rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
      <div className="p-4 flex gap-3">
        <img
          className="w-12 h-12 rounded object-cover flex-shrink-0"
          src={
            avatar_url ||
            `https://placehold.co/48x48/4AACC5/white?text=${displayName.charAt(0)}`
          }
          alt={displayName}
          onError={(e) => {
            e.target.src = `https://placehold.co/48x48/4AACC5/white?text=${displayName.charAt(0)}`;
          }}
        />
        <div className="flex flex-col items-start gap-1 min-w-0 flex-1">
          <div className="text-[#4AACC5] text-xs font-normal">#{user_id}</div>
          <div className="text-zinc-800 text-sm font-semibold">{displayName}</div>
          <div className="text-zinc-600 text-xs leading-tight">
            Program: {course_name}
          </div>
        </div>
      </div>

      <div className="px-4 pb-4 flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 flex-shrink-0 text-gray-500" />
          <div className="text-[#4AACC5] text-sm truncate">{email}</div>
        </div>

        <div className="flex items-center gap-2">
          <Phone className="w-4 h-4 flex-shrink-0 text-gray-500" />
          <div className="text-zinc-800 text-sm">{mobile_number}</div>
        </div>

        <div className="flex items-center gap-2 justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 flex-shrink-0 text-gray-500" />
            <div className="text-zinc-800 text-sm">{formattedDate}</div>
          </div>
          <button
            className="flex items-center text-[#4AACC5] text-sm font-medium hover:opacity-80 transition-opacity whitespace-nowrap"
            onClick={handleViewDetails}
          >
            View Details
            <ChevronRight className="w-4 h-4 ml-1" />
          </button>
        </div>
      </div>
    </div>
  );
};

// -------------------------------------------------------
// Empty State Component
// -------------------------------------------------------
const EmptyState = ({ isFiltered }) => (
  <div className="flex flex-col items-center justify-center py-20 px-4">
    <div className="w-20 h-20 rounded-full bg-cyan-50 flex items-center justify-center mb-4">
      <Users className="w-10 h-10 text-[#4AACC5] opacity-60" />
    </div>
    <h3 className="text-zinc-700 text-lg font-semibold mb-1">
      {isFiltered ? "No matching students" : "No students yet"}
    </h3>
    <p className="text-gray-400 text-sm text-center max-w-xs">
      {isFiltered
        ? "Try adjusting your search or clearing the date filter to see more results."
        : "Students will appear here once they enroll in your programs."}
    </p>
  </div>
);

// -------------------------------------------------------
// MAIN COMPONENT
// -------------------------------------------------------
export default function StudentManagement({
  students: allStudents = [],
  totalStudents = 0,
  filters = { name: "", email: "", phone: "" },
  onFiltersChange,
}) {
  const [startDate, setStartDate] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [resultsPerPage, setResultsPerPage] = useState(10);

  const isFiltered = !!(filters.name || filters.email || filters.phone || startDate);

  // Only date filter is client-side
  const filteredStudents = startDate
    ? allStudents.filter(
        (s) =>
          s.schedule_start_date &&
          new Date(s.schedule_start_date).toDateString() ===
            new Date(startDate).toDateString()
      )
    : allStudents;

  const total = filteredStudents.length;
  const totalPages = Math.ceil(total / resultsPerPage) || 1;
  const paginatedStudents = filteredStudents.slice(
    (currentPage - 1) * resultsPerPage,
    currentPage * resultsPerPage
  );

  const handleSearchChange = (e) => {
    setCurrentPage(1);
    const value = e.target.value;
    const isEmail = value.includes("@");
    const isPhone = /^\d+$/.test(value) && value.length > 0;

    if (isEmail) {
      onFiltersChange({ name: "", email: value, phone: "" });
    } else if (isPhone) {
      onFiltersChange({ name: "", email: "", phone: value });
    } else {
      onFiltersChange({ name: value, email: "", phone: "" });
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handleResultsPerPageChange = (e) => {
    setResultsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [filters, startDate]);

  const searchValue = filters.name || filters.email || filters.phone || "";

  const renderPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 3;

    if (totalPages <= maxPagesToShow + 1) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(
          <button
            key={i}
            onClick={() => handlePageChange(i)}
            className={`w-8 h-8 rounded-lg border flex items-center justify-center text-sm transition-colors ${
              currentPage === i
                ? "bg-[#4AACC5] border-[#4AACC5] text-white"
                : "bg-white text-zinc-800 border-stone-300 hover:bg-gray-50"
            }`}
          >
            {i}
          </button>
        );
      }
    } else {
      for (let i = 1; i <= maxPagesToShow; i++) {
        pages.push(
          <button
            key={i}
            onClick={() => handlePageChange(i)}
            className={`w-8 h-8 rounded-lg border flex items-center justify-center text-sm transition-colors ${
              currentPage === i
                ? "bg-[#4AACC5] border-[#4AACC5] text-white"
                : "bg-white text-zinc-800 border-stone-300 hover:bg-gray-50"
            }`}
          >
            {i}
          </button>
        );
      }
      pages.push(<span key="ellipsis" className="text-gray-700 px-2">...</span>);
      pages.push(
        <button
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
          className={`w-8 h-8 rounded-lg border flex items-center justify-center text-sm transition-colors ${
            currentPage === totalPages
              ? "bg-[#4AACC5] border-[#4AACC5] text-white"
              : "bg-white text-zinc-800 border-stone-300 hover:bg-gray-50"
          }`}
        >
          {totalPages}
        </button>
      );
    }

    return pages;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-zinc-800 text-xl font-bold mb-4">Students</h1>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            {/* Search */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search Name / Email / Mobile"
                value={searchValue}
                onChange={handleSearchChange}
                className="w-64 h-11 px-4 pr-10 bg-white rounded border border-sky-100 text-sm focus:outline-none focus:border-[#4AACC5] transition-colors"
              />
              <Search className="w-4 h-4 absolute right-3 top-3.5 text-gray-400" />
            </div>

            {/* Date Filter */}
            <div className="w-60 relative">
              <DatePicker
                selected={startDate}
                onChange={(date) => {
                  setCurrentPage(1);
                  setStartDate(date);
                }}
                dateFormat="MM/dd/yyyy"
                placeholderText="Filter by Start Date"
                calendarStartDay={0}
                wrapperClassName="w-full block"
                className="block w-full h-11 px-4 bg-white rounded border border-sky-100 text-sm focus:outline-none focus:ring-2 focus:ring-[#4AACC5]"
              />
              {startDate && (
                <button
                  onClick={() => {
                    setStartDate(null);
                    setCurrentPage(1);
                  }}
                  className="absolute right-3 top-3 text-gray-400 hover:text-red-500 text-sm"
                  title="Clear date"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Empty State */}
        {paginatedStudents.length === 0 ? (
          <EmptyState isFiltered={isFiltered} />
        ) : (
          <>
            {/* Student Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {paginatedStudents.map((student) => (
                <StudentCard
                  key={student.user_id || Math.random()}
                  data={student}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 0 && (
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="w-8 h-8 bg-white rounded-lg border border-stone-300 flex items-center justify-center hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4 text-gray-700" />
                  </button>

                  {renderPageNumbers()}

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="w-8 h-8 bg-white rounded-lg border border-stone-300 flex items-center justify-center hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="w-4 h-4 text-gray-700" />
                  </button>
                </div>

                <div className="flex items-center gap-5">
                  <span className="text-gray-700 text-sm font-medium">
                    Results {Math.min((currentPage - 1) * resultsPerPage + 1, total)}–
                    {Math.min(currentPage * resultsPerPage, total)} of {total}
                  </span>

                  <select
                    value={resultsPerPage}
                    onChange={handleResultsPerPageChange}
                    className="w-14 h-8 bg-gray-200 rounded-lg text-gray-700 text-sm font-medium px-2 cursor-pointer hover:bg-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-[#4AACC5]"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}